"use client";

import styles from "./styles.module.scss";
import strapiSingleAttribute from "@/api/strapiSingleAttribute";
import Button from "@/components/atoms/Button";
import Markdown from "@/components/atoms/Markdown";
import cn from "clsx";
import { useRouter } from "next/navigation";
import UnivoImage from "@/components/common/UnivoImage";
import UnivoCarousel from "@/components/common/UnivoCarousel";
import type { UnivoCarouselSettings } from "@/components/common/UnivoCarousel/types";

const listingBannerCarouselSettings: UnivoCarouselSettings = {
  slidesPerView: 1,
  spaceBetween: 8,
  autoplay: { status: true, delay: 6000 },
  pagination: {
    status: true,
    clickable: true,
    className: styles.pagination,
  },
  navigation: { status: false },
};

export default function ListingBanner({
  Banner,
  isDesktop,
  isNewsList = false,
}) {
  const slides = Banner?.filter((x) => x?.enabled) ?? [];

  return (
    <div className="container" id="blogs-listing-banner">
      <UnivoCarousel
        settings={listingBannerCarouselSettings}
        className={styles.carousel}
        slideWrapperClassName={styles.slide}
        isSmoothBehaviour
        id="blogs-listing-banner"
      >
        {slides.map((item, i) => (
          <BannerCard
            key={i}
            {...item}
            isDesktop={isDesktop}
            isNewsList={isNewsList}
            isFirstSlide={i === 0}
          />
        ))}
      </UnivoCarousel>
    </div>
  );
}

export function BannerCard({
  Image,
  Date,
  ReadingTimeText,
  NewsSource = null,
  Heading,
  CTA,
  isDesktop,
  isSingleBlog = false,
  isNewsList = false,
  isFirstSlide = false,
}) {
  const img = strapiSingleAttribute(
    isDesktop ? Image?.source : Image?.mobileSource
  );
  const router = useRouter();
  return (
    <div className={styles.imageContainer}>
      {img?.url ? (
        <UnivoImage
          src={img.url}
          alt={Image?.alt || ""}
          className={styles.image}
          width={1200}
          height={500}
          sizes="100vw"
          // Banner is above-the-fold LCP on /blog; first slide is eager/high,
          // later slides stay lazy. CSS width:100% + height:auto preserves
          // the previous intrinsic-height layout from raw <img>.
          isLcp={isFirstSlide}
          priority={isFirstSlide}
          loading={isFirstSlide ? undefined : "lazy"}
          style={{ width: "100%", height: "auto" }}
        />
      ) : null}
      {isSingleBlog ? (
        <UnivoImage
          src="/_a/icons/left-arrow-white-circle.svg"
          alt="go back"
          onClick={() => router.push("/amity/blog")}
          className={styles.backButton}
          width={38}
          height={38}
          sizes="38px"
          loading="lazy"
          fetchPriority="low"
        />
      ) : null}
      <div
        className={cn(styles.textContainer, {
          [styles.singleBlogTextContainer]: isSingleBlog,
        })}
      >
       <div
          className={(isNewsList && Date) ? cn(styles.dateContainer, {
            [styles.dateContainerNewsList]: isNewsList,
          }) : undefined}
        >
          <span>{Date}</span>
           <span
            className={cn({
              ["block lg:inline"]: isNewsList,
            })}
          >
            {isNewsList ? NewsSource : ReadingTimeText}
          </span>
        
        </div>
        {Heading?.enabled ? (
           <Markdown as="h1" className={styles.heading}>{Heading?.text}</Markdown>
        ) : null}
        {CTA?.enabled ? (
          <Button
            buttonProps={{
              href: CTA?.url || "#",
              target: CTA?.target?.[0] || "_self",
            }}
            icon="/_a/icons/left-arrow-white.svg"
            hoverIcon="/_a/icons/left-arrow.svg"
            bg="o"
            color="w"
          >
            {CTA?.text}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
